import { FiTrendingUp, FiPieChart, FiCopy, FiGift, FiBriefcase } from 'react-icons/fi';
import { useI18n } from '../i18n/useI18n';

const dockItems = [
  { icon: FiTrendingUp, key: 'leaderboard', labelKey: 'nav.leaderboard' },
  { icon: FiPieChart, key: 'positions', labelKey: 'nav.positions' },
  { icon: FiCopy, key: 'my-copy-trading', labelKey: 'nav.myCopyTrading' },
  { icon: FiBriefcase, key: 'wallets', labelKey: 'nav.wallets' },
  { icon: FiGift, key: 'invite', labelKey: 'nav.invite' }
];

export function Sidebar({
  dockOpen,
  activeSection,
  isAuthenticated,
  membershipTier,
  favoriteDetailMode,
  singleMarketMode,
  traderDetailMode,
  onClose,
  onNavigate,
  onSectionChange,
  onLoginOpen,
  onMembershipRequired,
  pushToast
}) {
  const { t } = useI18n();

  const handleNavClick = (target) => {
    onClose();

    if (target === 'leaderboard') {
      onNavigate('/');
      setTimeout(() => {
        onSectionChange('leaderboard');
      }, 0);
    } else if (target === 'positions') {
      if (!isAuthenticated) {
        onLoginOpen();
        return;
      }
      if (favoriteDetailMode || singleMarketMode || traderDetailMode) {
        onNavigate('/');
      }
      onSectionChange('positions');
    } else if (target === 'my-copy-trading') {
      if (!isAuthenticated) {
        onLoginOpen();
        return;
      }
      if (membershipTier && membershipTier.toLowerCase() === 'free') {
        onMembershipRequired?.();
        return;
      }
      if (favoriteDetailMode || singleMarketMode || traderDetailMode) {
        onNavigate('/');
      }
      onSectionChange('my-copy-trading');
    } else if (target === 'wallets') {
      if (!isAuthenticated) {
        onLoginOpen();
        return;
      }
      if (favoriteDetailMode || singleMarketMode || traderDetailMode) {
        onNavigate('/');
      }
      onSectionChange('wallets');
    } else if (target === 'invite') {
      if (!isAuthenticated) {
        onLoginOpen();
        return;
      }
      if (favoriteDetailMode || singleMarketMode || traderDetailMode) {
        onNavigate('/');
      }
      onSectionChange('invite');
    } else {
      const featureKey = dockItems.find((item) => item.key === target)?.labelKey;
      const featureLabel = featureKey ? t(featureKey) : target;
      pushToast(t('toast.comingSoon', { feature: featureLabel }), 'info');
    }
  };

  return (
    <>
      <aside
        className={`
          fixed
          top-[88px] md:top-[104px] lg:top-[120px]
          ${dockOpen ? 'left-4 opacity-100 pointer-events-auto' : '-left-[280px] opacity-0 pointer-events-none'}
          bottom-6 w-56
          transition-all duration-300 ease-out
          z-50 lg:z-10
          flex flex-col
        `}
      >
        {/* Sidebar Container */}
        <div className="glass-xl flex-1 rounded-3xl p-4 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="mb-4 px-2">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">{t('nav.menu') || 'Menu'}</p>
          </div>

          {/* Navigation */}
          <nav className="flex flex-col gap-1.5 flex-1 overflow-y-auto">
            {dockItems.map((item) => {
              const isActive = item.key === activeSection;

              return (
                <button
                  key={item.key}
                  className={`
                    group flex items-center gap-3 px-4 py-3 w-full
                    rounded-xl text-sm font-medium transition-all duration-200
                    relative overflow-hidden
                    ${isActive
                      ? 'bg-gradient-to-r from-sky-500/20 to-transparent text-white shadow-sm'
                      : 'text-slate-400 hover:text-white hover:bg-white/5'
                    }
                  `}
                  onClick={() => handleNavClick(item.key)}
                >
                  {/* Active Indicator */}
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-sky-500 rounded-r-full shadow-[0_0_10px_rgba(14,165,233,0.5)]" />
                  )}

                  {/* Icon Container */}
                  <div className={`
                    p-2 rounded-lg transition-all duration-200
                    ${isActive
                      ? 'bg-sky-500/20 text-sky-400'
                      : 'bg-slate-800/50 text-slate-500 group-hover:bg-slate-700/50 group-hover:text-slate-300'
                    }
                  `}>
                    <item.icon className="w-4 h-4" />
                  </div>

                  {/* Label */}
                  <span className="flex-1 text-left">{t(item.labelKey)}</span>

                  {/* Active Dot */}
                  {isActive && (
                    <div className="w-1.5 h-1.5 rounded-full bg-sky-400 shadow-[0_0_6px_rgba(56,189,248,0.6)]" />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="mt-4 pt-4 border-t border-slate-700/30 px-2">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span>{t('copyTrading.systemOnline')}</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Overlay for Mobile */}
      <div
        className={`
          fixed inset-0 bg-slate-950/70 backdrop-blur-sm z-[45] lg:hidden transition-opacity duration-300
          ${dockOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
        onClick={onClose}
      />
    </>
  );
}
